/**
 * SiteSettingsPage - Full admin dashboard for site branding, typography, colors, and themes
 * Located at /admin/site-settings
 */
import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth, API } from "../App";
import { useTheme } from "../contexts/ThemeContext";
import axios from "axios";
import { toast } from "sonner";
import AdminSidebar from "../components/layout/AdminSidebar";

import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Slider } from "../components/ui/slider";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "../components/ui/dialog";
import { ScrollArea } from "../components/ui/scroll-area";
import { Separator } from "../components/ui/separator";

import {
  Palette,
  Type,
  Image,
  Layers,
  Film,
  Sparkles,
  Save,
  RotateCcw,
  Upload,
  Copy,
  Trash2,
  Play,
  Plus,
  Eye,
  Loader2,
  ArrowLeft,
  Mountain,
} from "lucide-react";

// Color picker component - defined outside main component
const ColorPicker = ({ label, value, onChange }) => (
  <div className="flex items-center gap-3">
    <div 
      className="w-10 h-10 rounded-lg border-2 border-border cursor-pointer shadow-sm"
      style={{ backgroundColor: value }}
      onClick={() => document.getElementById(`color-${label}`)?.click()}
    />
    <input
      id={`color-${label}`}
      type="color"
      value={value || "#000000"}
      onChange={(e) => onChange(e.target.value)}
      className="sr-only"
    />
    <div className="flex-1">
      <Label className="text-sm">{label}</Label>
      <Input 
        value={value || ""} 
        onChange={(e) => onChange(e.target.value)}
        placeholder="#000000"
        className="mt-1 font-mono text-sm h-8"
      />
    </div>
  </div>
);

// Preview Panel component - defined outside main component
const PreviewPanel = ({ editColors, editTypography, editBranding, editUIComponents }) => (
  <Card className="sticky top-4">
    <CardHeader className="pb-3">
      <CardTitle className="text-base flex items-center gap-2">
        <Eye className="w-4 h-4" />
        Live Preview
      </CardTitle>
    </CardHeader>
    <CardContent className="space-y-4">
      <div 
        className="rounded-lg p-4 space-y-3"
        style={{ 
          backgroundColor: editColors.background_color,
          color: editColors.text_color,
          fontFamily: editTypography.body_font,
        }}
      >
        <h3 
          className="text-lg font-semibold"
          style={{ fontFamily: editTypography.heading_font }}
        >
          {editBranding.site_name || "Site Name"}
        </h3>
        <p className="text-sm" style={{ color: editColors.text_muted_color }}>
          {editBranding.site_tagline || "Tagline"}
        </p>
        <div className="flex gap-2">
          <button
            className="px-3 py-1.5 rounded text-sm text-white"
            style={{ 
              backgroundColor: editColors.button_primary_color,
              borderRadius: `${(editUIComponents.border_radius_scale || 1) * 6}px`
            }}
          >
            Primary
          </button>
          <button
            className="px-3 py-1.5 rounded text-sm text-white"
            style={{ 
              backgroundColor: editColors.button_secondary_color || editColors.secondary_color,
              borderRadius: `${(editUIComponents.border_radius_scale || 1) * 6}px`
            }}
          >
            Secondary
          </button>
        </div>
        <div 
          className="p-3 rounded"
          style={{ 
            backgroundColor: editColors.surface_color,
            borderRadius: `${(editUIComponents.border_radius_scale || 1) * 8}px`,
            boxShadow: `0 ${(editUIComponents.shadow_scale || 1) * 4}px ${(editUIComponents.shadow_scale || 1) * 8}px rgba(0,0,0,0.1)`
          }}
        >
          <p className="text-sm">Card content preview</p>
          <span style={{ color: editColors.link_color }} className="text-sm cursor-pointer">Link example</span>
        </div>
        <div className="flex gap-2 text-xs">
          <Badge style={{ backgroundColor: editColors.status_success_color }}>Success</Badge>
          <Badge style={{ backgroundColor: editColors.status_warning_color }}>Warning</Badge>
          <Badge style={{ backgroundColor: editColors.status_error_color }}>Error</Badge>
        </div>
      </div>
    </CardContent>
  </Card>
);

const SiteSettingsPage = () => {
  const navigate = useNavigate();
  const { token } = useAuth();
  const { refreshTheme } = useTheme();
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState(null);
  const [themes, setThemes] = useState([]);
  const [fonts, setFonts] = useState([]);
  const [activeTab, setActiveTab] = useState("branding");
  
  // Edit state - local changes before saving
  const [editBranding, setEditBranding] = useState({});
  const [editTypography, setEditTypography] = useState({});
  const [editColors, setEditColors] = useState({});
  const [editBackgrounds, setEditBackgrounds] = useState({});
  const [editUIComponents, setEditUIComponents] = useState({});
  const [editCinematic, setEditCinematic] = useState({});
  
  // Theme management
  const [selectedTheme, setSelectedTheme] = useState(null);
  const [themeDialogOpen, setThemeDialogOpen] = useState(false);
  const [newThemeName, setNewThemeName] = useState("");
  const [newThemeDesc, setNewThemeDesc] = useState("");

  const headers = { Authorization: `Bearer ${token}` };

  // Fetch data
  const fetchData = useCallback(async () => {
    try {
      const [settingsRes, themesRes, fontsRes] = await Promise.all([
        axios.get(`${API}/site-settings`, { headers }),
        axios.get(`${API}/themes`, { headers }),
        axios.get(`${API}/fonts`),
      ]);
      
      setSettings(settingsRes.data);
      setThemes(themesRes.data);
      setFonts(fontsRes.data);
      
      // Initialize edit states from settings or active theme
      const activeTheme = settingsRes.data.active_theme || {};
      setEditBranding(settingsRes.data.branding || activeTheme.branding || {});
      setEditTypography(settingsRes.data.typography || activeTheme.typography || {});
      setEditColors(settingsRes.data.colors || activeTheme.colors || {});
      setEditBackgrounds(settingsRes.data.backgrounds || activeTheme.backgrounds || {});
      setEditUIComponents(settingsRes.data.ui_components || activeTheme.ui_components || {});
      setEditCinematic(settingsRes.data.cinematic_view || activeTheme.cinematic_view || {});
      
    } catch (error) {
      toast.error("Failed to load settings");
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Save settings
  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/site-settings`, {
        branding: editBranding,
        typography: editTypography,
        colors: editColors,
        backgrounds: editBackgrounds,
        ui_components: editUIComponents,
        cinematic_view: editCinematic,
      }, { headers });
      
      toast.success("Settings saved!");
      refreshTheme();
      fetchData();
    } catch (error) {
      toast.error("Failed to save settings");
    } finally {
      setSaving(false);
    }
  };

  // Reset to default
  const handleReset = async () => {
    if (!window.confirm("Reset all settings to default theme?")) return;
    
    try {
      await axios.post(`${API}/site-settings/reset`, {}, { headers });
      toast.success("Settings reset to default");
      refreshTheme();
      fetchData();
    } catch (error) {
      toast.error("Failed to reset settings");
    }
  };

  // Upload image
  const handleUploadImage = async (file, imageType) => {
    const formData = new FormData();
    formData.append("file", file);
    formData.append("image_type", imageType);
    
    try {
      const response = await axios.post(
        `${API}/site-settings/upload-image?image_type=${imageType}`,
        formData,
        { headers: { ...headers, "Content-Type": "multipart/form-data" } }
      );
      return response.data.url;
    } catch (error) {
      toast.error("Failed to upload image");
      return null;
    }
  };

  // Theme actions
  const handleActivateTheme = async (themeId) => {
    try {
      await axios.put(`${API}/themes/${themeId}/activate`, {}, { headers });
      toast.success("Theme activated!");
      refreshTheme();
      fetchData();
    } catch (error) {
      toast.error("Failed to activate theme");
    }
  };

  const handleDuplicateTheme = async (themeId, name) => {
    try {
      await axios.post(`${API}/themes/${themeId}/duplicate?new_name=${encodeURIComponent(name)}`, {}, { headers });
      toast.success("Theme duplicated!");
      fetchData();
    } catch (error) {
      toast.error("Failed to duplicate theme");
    }
  };

  const handleDeleteTheme = async (themeId) => {
    if (!window.confirm("Delete this theme?")) return;
    
    try {
      await axios.delete(`${API}/themes/${themeId}`, { headers });
      toast.success("Theme deleted");
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete theme");
    }
  };

  const handleCreateTheme = async () => {
    if (!newThemeName.trim()) {
      toast.error("Please enter a theme name");
      return;
    }
    
    try {
      await axios.post(`${API}/themes`, {
        name: newThemeName,
        description: newThemeDesc,
        branding: editBranding,
        typography: editTypography,
        colors: editColors,
        backgrounds: editBackgrounds,
        ui_components: editUIComponents,
        cinematic_view: editCinematic,
      }, { headers });
      
      toast.success("Theme created!");
      setThemeDialogOpen(false);
      setNewThemeName("");
      setNewThemeDesc("");
      fetchData();
    } catch (error) {
      toast.error("Failed to create theme");
    }
  };

  // Sidebar navigation items
  const sidebarItems = [
    { id: "branding", label: "Branding", icon: Mountain, description: "Site name & logos" },
    { id: "typography", label: "Typography", icon: Type, description: "Fonts & text styling" },
    { id: "colors", label: "Colors", icon: Palette, description: "Color palette" },
    { id: "backgrounds", label: "Backgrounds", icon: Image, description: "Images & overlays" },
    { id: "ui", label: "UI Components", icon: Layers, description: "Radius, shadows, spacing" },
    { id: "cinematic", label: "Cinematic", icon: Film, description: "Presentation mode" },
    { id: "themes", label: "Themes", icon: Sparkles, description: "Manage saved themes" },
  ];

  if (loading) {
    return (
      <AdminSidebar>
        <div className="min-h-screen bg-background flex items-center justify-center">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </div>
      </AdminSidebar>
    );
  }

  // Content renderer based on active section
  const renderContent = () => {
    switch (activeTab) {
      case "branding":
        return (
          <Card>
            <CardHeader>
              <CardTitle>Brand Identity</CardTitle>
              <CardDescription>Configure your site name and logos</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label>Site Name</Label>
                  <Input
                    value={editBranding.site_name || ""}
                    onChange={(e) => setEditBranding({ ...editBranding, site_name: e.target.value })}
                    placeholder="Horse Mountain Ranch"
                  />
                </div>
                <div>
                  <Label>Tagline</Label>
                  <Input
                    value={editBranding.site_tagline || ""}
                    onChange={(e) => setEditBranding({ ...editBranding, site_tagline: e.target.value })}
                    placeholder="Homeowners Association"
                  />
                </div>
              </div>
              
              <Separator />
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Light Logo */}
                <div>
                  <Label>Logo (Light Background)</Label>
                  <div className="mt-2 border-2 border-dashed rounded-lg p-4 text-center">
                    {editBranding.logo_light_url ? (
                      <img src={editBranding.logo_light_url} alt="Light logo" className="h-16 mx-auto mb-2" />
                    ) : (
                      <Upload className="w-8 h-8 mx-auto text-muted-foreground mb-2" />
                    )}
                    <Input
                      type="file"
                      accept="image/*"
                      onChange={async (e) => {
                        const url = await handleUploadImage(e.target.files[0], "logo_light");
                        if (url) setEditBranding({ ...editBranding, logo_light_url: url });
                      }}
                      className="hidden"
                      id="logo-light-upload"
                    />
                    <Button variant="outline" size="sm" onClick={() => document.getElementById("logo-light-upload")?.click()}>
                      Upload
                    </Button>
                  </div>
                </div>
                
                {/* Dark Logo */}
                <div>
                  <Label>Logo (Dark Background)</Label>
                  <div className="mt-2 border-2 border-dashed rounded-lg p-4 text-center bg-gray-900">
                    {editBranding.logo_dark_url ? (
                      <img src={editBranding.logo_dark_url} alt="Dark logo" className="h-16 mx-auto mb-2" />
                    ) : (
                      <Upload className="w-8 h-8 mx-auto text-gray-500 mb-2" />
                    )}
                    <Input
                      type="file"
                      accept="image/*"
                      onChange={async (e) => {
                        const url = await handleUploadImage(e.target.files[0], "logo_dark");
                        if (url) setEditBranding({ ...editBranding, logo_dark_url: url });
                      }}
                      className="hidden"
                      id="logo-dark-upload"
                    />
                    <Button variant="outline" size="sm" onClick={() => document.getElementById("logo-dark-upload")?.click()}>
                      Upload
                    </Button>
                  </div>
                </div>
                
                {/* Favicon */}
                <div>
                  <Label>Favicon</Label>
                  <div className="mt-2 border-2 border-dashed rounded-lg p-4 text-center">
                    {editBranding.favicon_url ? (
                      <img src={editBranding.favicon_url} alt="Favicon" className="h-8 mx-auto mb-2" />
                    ) : (
                      <Upload className="w-6 h-6 mx-auto text-muted-foreground mb-2" />
                    )}
                    <Input
                      type="file"
                      accept="image/*,.ico"
                      onChange={async (e) => {
                        const url = await handleUploadImage(e.target.files[0], "favicon");
                        if (url) setEditBranding({ ...editBranding, favicon_url: url });
                      }}
                      className="hidden"
                      id="favicon-upload"
                    />
                    <Button variant="outline" size="sm" onClick={() => document.getElementById("favicon-upload")?.click()}>
                      Upload
                    </Button>
                  </div>
                </div>
                
                {/* Email Logo */}
                <div>
                  <Label>Email Header Logo</Label>
                  <div className="mt-2 border-2 border-dashed rounded-lg p-4 text-center">
                    {editBranding.email_header_logo_url ? (
                      <img src={editBranding.email_header_logo_url} alt="Email logo" className="h-12 mx-auto mb-2" />
                    ) : (
                      <Upload className="w-6 h-6 mx-auto text-muted-foreground mb-2" />
                    )}
                    <Input
                      type="file"
                      accept="image/*"
                      onChange={async (e) => {
                        const url = await handleUploadImage(e.target.files[0], "email_header");
                        if (url) setEditBranding({ ...editBranding, email_header_logo_url: url });
                      }}
                      className="hidden"
                      id="email-logo-upload"
                    />
                    <Button variant="outline" size="sm" onClick={() => document.getElementById("email-logo-upload")?.click()}>
                      Upload
                    </Button>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        );

      case "typography":
        return (
          <Card>
            <CardHeader>
              <CardTitle>Typography</CardTitle>
              <CardDescription>Configure fonts and text styling</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <Label>Heading Font</Label>
                  <Select
                    value={editTypography.heading_font}
                    onValueChange={(v) => setEditTypography({ ...editTypography, heading_font: v })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select font" />
                    </SelectTrigger>
                    <SelectContent>
                      {fonts.map((font) => (
                        <SelectItem key={font.name} value={font.name}>
                          <span style={{ fontFamily: font.name }}>{font.name}</span>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-muted-foreground mt-1">Used for titles and headings</p>
                </div>
                
                <div>
                  <Label>Body Font</Label>
                  <Select
                    value={editTypography.body_font}
                    onValueChange={(v) => setEditTypography({ ...editTypography, body_font: v })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select font" />
                    </SelectTrigger>
                    <SelectContent>
                      {fonts.map((font) => (
                        <SelectItem key={font.name} value={font.name}>
                          <span style={{ fontFamily: font.name }}>{font.name}</span>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-muted-foreground mt-1">Used for body text</p>
                </div>
                
                <div>
                  <Label>Accent Font</Label>
                  <Select
                    value={editTypography.accent_font}
                    onValueChange={(v) => setEditTypography({ ...editTypography, accent_font: v })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select font" />
                    </SelectTrigger>
                    <SelectContent>
                      {fonts.map((font) => (
                        <SelectItem key={font.name} value={font.name}>
                          <span style={{ fontFamily: font.name }}>{font.name}</span>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-muted-foreground mt-1">Used for special elements</p>
                </div>
              </div>
              
              <Separator />
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <Label>Font Scale: {editTypography.font_scale || 1}</Label>
                  <Slider
                    value={[editTypography.font_scale || 1]}
                    min={0.8}
                    max={1.2}
                    step={0.05}
                    onValueChange={([v]) => setEditTypography({ ...editTypography, font_scale: v })}
                    className="mt-2"
                  />
                  <p className="text-xs text-muted-foreground mt-1">Scales all text sizes</p>
                </div>
                
                <div>
                  <Label>Font Weight Scale: {editTypography.font_weight_scale || 1}</Label>
                  <Slider
                    value={[editTypography.font_weight_scale || 1]}
                    min={0.8}
                    max={1.2}
                    step={0.05}
                    onValueChange={([v]) => setEditTypography({ ...editTypography, font_weight_scale: v })}
                    className="mt-2"
                  />
                  <p className="text-xs text-muted-foreground mt-1">Adjusts text boldness</p>
                </div>
              </div>
              
              {/* Font Preview */}
              <div className="p-4 bg-muted/50 rounded-lg space-y-2">
                <h3 className="text-2xl font-bold" style={{ fontFamily: editTypography.heading_font }}>
                  Heading Preview (The quick brown fox)
                </h3>
                <p style={{ fontFamily: editTypography.body_font }}>
                  Body text preview. The quick brown fox jumps over the lazy dog.
                </p>
                <p className="italic" style={{ fontFamily: editTypography.accent_font }}>
                  Accent text preview - elegant and refined.
                </p>
              </div>
            </CardContent>
          </Card>
        );

      case "colors":
        return (
          <Card>
            <CardHeader>
              <CardTitle>Color Palette</CardTitle>
              <CardDescription>Configure colors for your brand</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-4">
                {/* Primary Colors */}
                <div className="space-y-4">
                  <h4 className="font-medium text-sm text-muted-foreground">Primary Colors</h4>
                  <ColorPicker label="Primary" value={editColors.primary_color} onChange={(v) => setEditColors({ ...editColors, primary_color: v })} />
                  <ColorPicker label="Secondary" value={editColors.secondary_color} onChange={(v) => setEditColors({ ...editColors, secondary_color: v })} />
                  <ColorPicker label="Accent" value={editColors.accent_color} onChange={(v) => setEditColors({ ...editColors, accent_color: v })} />
                </div>
                
                {/* Background Colors */}
                <div className="space-y-4">
                  <h4 className="font-medium text-sm text-muted-foreground">Background Colors</h4>
                  <ColorPicker label="Background" value={editColors.background_color} onChange={(v) => setEditColors({ ...editColors, background_color: v })} />
                  <ColorPicker label="Surface" value={editColors.surface_color} onChange={(v) => setEditColors({ ...editColors, surface_color: v })} />
                  <ColorPicker label="Border" value={editColors.border_color} onChange={(v) => setEditColors({ ...editColors, border_color: v })} />
                </div>
                
                {/* Text Colors */}
                <div className="space-y-4">
                  <h4 className="font-medium text-sm text-muted-foreground">Text Colors</h4>
                  <ColorPicker label="Text" value={editColors.text_color} onChange={(v) => setEditColors({ ...editColors, text_color: v })} />
                  <ColorPicker label="Muted Text" value={editColors.text_muted_color} onChange={(v) => setEditColors({ ...editColors, text_muted_color: v })} />
                  <ColorPicker label="Link" value={editColors.link_color} onChange={(v) => setEditColors({ ...editColors, link_color: v })} />
                </div>
                
                {/* Button & Status Colors */}
                <div className="space-y-4">
                  <h4 className="font-medium text-sm text-muted-foreground">Buttons & Status</h4>
                  <ColorPicker label="Primary Button" value={editColors.button_primary_color} onChange={(v) => setEditColors({ ...editColors, button_primary_color: v })} />
                  <ColorPicker label="Success" value={editColors.status_success_color} onChange={(v) => setEditColors({ ...editColors, status_success_color: v })} />
                  <ColorPicker label="Warning" value={editColors.status_warning_color} onChange={(v) => setEditColors({ ...editColors, status_warning_color: v })} />
                  <ColorPicker label="Error" value={editColors.status_error_color} onChange={(v) => setEditColors({ ...editColors, status_error_color: v })} />
                </div>
              </div>
            </CardContent>
          </Card>
        );

      case "backgrounds":
        return (
          <Card>
            <CardHeader>
              <CardTitle>Backgrounds</CardTitle>
              <CardDescription>Configure background images and overlays</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Global Background */}
                <div>
                  <Label>Global Background Image</Label>
                  <div className="mt-2 border-2 border-dashed rounded-lg p-4 text-center min-h-[120px] flex flex-col items-center justify-center">
                    {editBackgrounds.global_background_image_url ? (
                      <div className="relative w-full">
                        <img src={editBackgrounds.global_background_image_url} alt="Background" className="w-full h-24 object-cover rounded" />
                        <Button variant="destructive" size="sm" className="absolute top-1 right-1" onClick={() => setEditBackgrounds({ ...editBackgrounds, global_background_image_url: null })}>
                          <Trash2 className="w-3 h-3" />
                        </Button>
                      </div>
                    ) : (
                      <Upload className="w-8 h-8 text-muted-foreground mb-2" />
                    )}
                    <Input type="file" accept="image/*" onChange={async (e) => { const url = await handleUploadImage(e.target.files[0], "global_background"); if (url) setEditBackgrounds({ ...editBackgrounds, global_background_image_url: url }); }} className="hidden" id="global-bg-upload" />
                    <Button variant="outline" size="sm" onClick={() => document.getElementById("global-bg-upload")?.click()}>Upload</Button>
                  </div>
                </div>
                
                {/* Login Background */}
                <div>
                  <Label>Login Page Background</Label>
                  <div className="mt-2 border-2 border-dashed rounded-lg p-4 text-center min-h-[120px] flex flex-col items-center justify-center">
                    {editBackgrounds.login_background_image_url ? (
                      <div className="relative w-full">
                        <img src={editBackgrounds.login_background_image_url} alt="Login Background" className="w-full h-24 object-cover rounded" />
                        <Button variant="destructive" size="sm" className="absolute top-1 right-1" onClick={() => setEditBackgrounds({ ...editBackgrounds, login_background_image_url: null })}>
                          <Trash2 className="w-3 h-3" />
                        </Button>
                      </div>
                    ) : (
                      <Upload className="w-8 h-8 text-muted-foreground mb-2" />
                    )}
                    <Input type="file" accept="image/*" onChange={async (e) => { const url = await handleUploadImage(e.target.files[0], "login_background"); if (url) setEditBackgrounds({ ...editBackgrounds, login_background_image_url: url }); }} className="hidden" id="login-bg-upload" />
                    <Button variant="outline" size="sm" onClick={() => document.getElementById("login-bg-upload")?.click()}>Upload</Button>
                  </div>
                </div>
              </div>
              
              <Separator />
              
              {/* Overlay Controls */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <Label>Overlay Color</Label>
                  <div className="flex items-center gap-3 mt-2">
                    <div className="w-10 h-10 rounded border cursor-pointer" style={{ backgroundColor: editBackgrounds.global_background_overlay_color }} onClick={() => document.getElementById("overlay-color")?.click()} />
                    <input id="overlay-color" type="color" value={editBackgrounds.global_background_overlay_color || "#000000"} onChange={(e) => setEditBackgrounds({ ...editBackgrounds, global_background_overlay_color: e.target.value })} className="sr-only" />
                    <Input value={editBackgrounds.global_background_overlay_color || "#000000"} onChange={(e) => setEditBackgrounds({ ...editBackgrounds, global_background_overlay_color: e.target.value })} className="flex-1 font-mono" />
                  </div>
                </div>
                
                <div>
                  <Label>Overlay Opacity: {editBackgrounds.global_background_overlay_opacity || 0}</Label>
                  <Slider value={[editBackgrounds.global_background_overlay_opacity || 0]} min={0} max={1} step={0.05} onValueChange={([v]) => setEditBackgrounds({ ...editBackgrounds, global_background_overlay_opacity: v })} className="mt-4" />
                </div>
              </div>
            </CardContent>
          </Card>
        );

      case "ui":
        return (
          <Card>
            <CardHeader>
              <CardTitle>UI Components</CardTitle>
              <CardDescription>Adjust visual styling of UI elements</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div>
                  <Label>Border Radius Scale: {editUIComponents.border_radius_scale || 1}</Label>
                  <Slider value={[editUIComponents.border_radius_scale || 1]} min={0.25} max={2} step={0.25} onValueChange={([v]) => setEditUIComponents({ ...editUIComponents, border_radius_scale: v })} className="mt-2" />
                  <div className="mt-4 flex gap-2">
                    {[0.25, 0.5, 1, 1.5, 2].map((s) => (
                      <div key={s} className="w-12 h-12 bg-primary" style={{ borderRadius: `${s * 8}px` }} />
                    ))}
                  </div>
                </div>
                
                <div>
                  <Label>Shadow Scale: {editUIComponents.shadow_scale || 1}</Label>
                  <Slider value={[editUIComponents.shadow_scale || 1]} min={0} max={2} step={0.25} onValueChange={([v]) => setEditUIComponents({ ...editUIComponents, shadow_scale: v })} className="mt-2" />
                  <div className="mt-4 flex gap-2">
                    {[0, 0.5, 1, 1.5, 2].map((s) => (
                      <div key={s} className="w-12 h-12 bg-card rounded" style={{ boxShadow: `0 ${s * 4}px ${s * 12}px rgba(0,0,0,${s * 0.1})` }} />
                    ))}
                  </div>
                </div>
                
                <div>
                  <Label>Spacing Scale: {editUIComponents.spacing_scale || 1}</Label>
                  <Slider value={[editUIComponents.spacing_scale || 1]} min={0.8} max={1.5} step={0.1} onValueChange={([v]) => setEditUIComponents({ ...editUIComponents, spacing_scale: v })} className="mt-2" />
                  <p className="text-xs text-muted-foreground mt-2">Affects padding and margins</p>
                </div>
              </div>
              
              <Separator />
              
              <div>
                <Label>Icon Set</Label>
                <Select value={editUIComponents.icon_set || "lucide"} onValueChange={(v) => setEditUIComponents({ ...editUIComponents, icon_set: v })}>
                  <SelectTrigger className="w-48">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="lucide">Lucide Icons</SelectItem>
                    <SelectItem value="heroicons">Heroicons</SelectItem>
                    <SelectItem value="fontawesome">Font Awesome</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </CardContent>
          </Card>
        );

      case "cinematic":
        return (
          <Card>
            <CardHeader>
              <CardTitle>Cinematic View</CardTitle>
              <CardDescription>Override settings for cinematic/immersive presentations</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <Label>Cinematic Heading Font (Override)</Label>
                  <Select value={editCinematic.cinematic_heading_font || "__default__"} onValueChange={(v) => setEditCinematic({ ...editCinematic, cinematic_heading_font: v === "__default__" ? null : v })}>
                    <SelectTrigger><SelectValue placeholder="Use default heading font" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__default__">Use Default</SelectItem>
                      {fonts.map((font) => (<SelectItem key={font.name} value={font.name}>{font.name}</SelectItem>))}
                    </SelectContent>
                  </Select>
                </div>
                
                <div>
                  <Label>Cinematic Body Font (Override)</Label>
                  <Select value={editCinematic.cinematic_body_font || "__default__"} onValueChange={(v) => setEditCinematic({ ...editCinematic, cinematic_body_font: v === "__default__" ? null : v })}>
                    <SelectTrigger><SelectValue placeholder="Use default body font" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__default__">Use Default</SelectItem>
                      {fonts.map((font) => (<SelectItem key={font.name} value={font.name}>{font.name}</SelectItem>))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              
              <Separator />
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <Label>Cinematic Overlay Color</Label>
                  <div className="flex items-center gap-3 mt-2">
                    <div className="w-10 h-10 rounded border cursor-pointer" style={{ backgroundColor: editCinematic.cinematic_overlay_color }} onClick={() => document.getElementById("cinematic-overlay-color")?.click()} />
                    <input id="cinematic-overlay-color" type="color" value={editCinematic.cinematic_overlay_color || "#000000"} onChange={(e) => setEditCinematic({ ...editCinematic, cinematic_overlay_color: e.target.value })} className="sr-only" />
                    <Input value={editCinematic.cinematic_overlay_color || "#000000"} onChange={(e) => setEditCinematic({ ...editCinematic, cinematic_overlay_color: e.target.value })} className="flex-1 font-mono" />
                  </div>
                </div>
                
                <div>
                  <Label>Cinematic Overlay Opacity: {editCinematic.cinematic_overlay_opacity || 0.6}</Label>
                  <Slider value={[editCinematic.cinematic_overlay_opacity || 0.6]} min={0} max={1} step={0.05} onValueChange={([v]) => setEditCinematic({ ...editCinematic, cinematic_overlay_opacity: v })} className="mt-4" />
                </div>
              </div>
            </CardContent>
          </Card>
        );

      case "themes":
        return (
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle>Saved Themes</CardTitle>
                  <CardDescription>Manage and switch between themes</CardDescription>
                </div>
                <Button onClick={() => setThemeDialogOpen(true)}>
                  <Plus className="w-4 h-4 mr-2" />
                  Save as Theme
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 gap-4">
                {themes.map((theme) => (
                  <div key={theme.theme_id} className={`p-4 border rounded-lg ${theme.is_active ? "border-primary bg-primary/5" : ""}`}>
                    <div className="flex items-start justify-between mb-3">
                      <div>
                        <h4 className="font-medium flex items-center gap-2">
                          {theme.name}
                          {theme.is_active && <Badge variant="default" className="text-xs">Active</Badge>}
                          {theme.is_system && <Badge variant="secondary" className="text-xs">System</Badge>}
                        </h4>
                        <p className="text-sm text-muted-foreground mt-1">{theme.description}</p>
                      </div>
                    </div>
                    
                    {/* Color preview */}
                    <div className="flex gap-1 mb-3">
                      <div className="w-6 h-6 rounded" style={{ backgroundColor: theme.colors?.primary_color }} title="Primary" />
                      <div className="w-6 h-6 rounded" style={{ backgroundColor: theme.colors?.secondary_color }} title="Secondary" />
                      <div className="w-6 h-6 rounded" style={{ backgroundColor: theme.colors?.accent_color }} title="Accent" />
                      <div className="w-6 h-6 rounded border" style={{ backgroundColor: theme.colors?.background_color }} title="Background" />
                      <div className="w-6 h-6 rounded border" style={{ backgroundColor: theme.colors?.surface_color }} title="Surface" />
                    </div>
                    
                    {/* Actions */}
                    <div className="flex gap-2">
                      {!theme.is_active && (
                        <Button size="sm" onClick={() => handleActivateTheme(theme.theme_id)}>
                          <Play className="w-3 h-3 mr-1" />
                          Activate
                        </Button>
                      )}
                      <Button size="sm" variant="outline" onClick={() => handleDuplicateTheme(theme.theme_id, `${theme.name} (Copy)`)}>
                        <Copy className="w-3 h-3 mr-1" />
                        Duplicate
                      </Button>
                      {!theme.is_system && !theme.is_active && (
                        <Button size="sm" variant="destructive" onClick={() => handleDeleteTheme(theme.theme_id)}>
                          <Trash2 className="w-3 h-3" />
                        </Button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        );

      default:
        return null;
    }
  };

  return (
    <AdminSidebar>
      <div className="min-h-screen bg-background" data-testid="site-settings-page">
        {/* Header */}
        <header className="border-b bg-card sticky top-0 z-10">
          <div className="px-4 md:px-6 py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Button variant="ghost" size="icon" onClick={() => navigate("/admin")}>
                  <ArrowLeft className="w-5 h-5" />
                </Button>
                <div>
                  <h1 className="font-serif text-xl font-semibold">Site Settings & Themes</h1>
                  <p className="text-sm text-muted-foreground">Customize branding, colors, fonts, and themes</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Button variant="outline" onClick={handleReset}>
                  <RotateCcw className="w-4 h-4 mr-2" />
                  Reset
                </Button>
                <Button onClick={handleSave} disabled={saving}>
                  {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                  Save Changes
                </Button>
              </div>
            </div>
          </div>
        </header>

        {/* Main Content with Sidebar Navigation */}
        <div className="flex">
          {/* Settings Sidebar Navigation */}
          <aside className="w-64 border-r bg-card min-h-[calc(100vh-73px)] sticky top-[73px] hidden md:block">
            <ScrollArea className="h-[calc(100vh-73px)]">
              <nav className="p-4 space-y-1">
                {sidebarItems.map((item) => {
                  const Icon = item.icon;
                  const isActive = activeTab === item.id;
                  return (
                    <button
                      key={item.id}
                      onClick={() => setActiveTab(item.id)}
                      className={`w-full flex items-start gap-3 px-3 py-3 rounded-lg text-left transition-colors ${
                        isActive 
                          ? "bg-primary/10 text-primary border border-primary/20" 
                          : "hover:bg-muted text-muted-foreground hover:text-foreground"
                      }`}
                      data-testid={`settings-nav-${item.id}`}
                    >
                      <Icon className={`w-5 h-5 mt-0.5 ${isActive ? "text-primary" : ""}`} />
                      <div>
                        <div className={`font-medium text-sm ${isActive ? "text-primary" : "text-foreground"}`}>
                          {item.label}
                        </div>
                        <div className="text-xs text-muted-foreground">{item.description}</div>
                      </div>
                    </button>
                  );
                })}
              </nav>
            </ScrollArea>
          </aside>

          {/* Mobile Nav */}
          <div className="md:hidden w-full border-b bg-card p-2 overflow-x-auto">
            <div className="flex gap-1 min-w-max">
              {sidebarItems.map((item) => {
                const Icon = item.icon;
                const isActive = activeTab === item.id;
                return (
                  <button
                    key={item.id}
                    onClick={() => setActiveTab(item.id)}
                    className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm whitespace-nowrap transition-colors ${
                      isActive ? "bg-primary text-primary-foreground" : "hover:bg-muted"
                    }`}
                  >
                    <Icon className="w-4 h-4" />
                    {item.label}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Content Area */}
          <main className="flex-1 p-4 md:p-6">
            <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
              {/* Settings Content */}
              <div className="xl:col-span-2">
                {renderContent()}
              </div>
              
              {/* Preview Panel */}
              <div className="hidden xl:block">
                <PreviewPanel 
                  editColors={editColors}
                  editTypography={editTypography}
                  editBranding={editBranding}
                  editUIComponents={editUIComponents}
                />
              </div>
            </div>
          </main>
        </div>

        {/* Create Theme Dialog */}
        <Dialog open={themeDialogOpen} onOpenChange={setThemeDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Save Current Settings as Theme</DialogTitle>
              <DialogDescription>Create a new theme from your current settings configuration</DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div>
                <Label>Theme Name</Label>
                <Input value={newThemeName} onChange={(e) => setNewThemeName(e.target.value)} placeholder="My Custom Theme" />
              </div>
              <div>
                <Label>Description</Label>
                <Input value={newThemeDesc} onChange={(e) => setNewThemeDesc(e.target.value)} placeholder="A brief description of this theme" />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setThemeDialogOpen(false)}>Cancel</Button>
              <Button onClick={handleCreateTheme}>Create Theme</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </AdminSidebar>
  );
};

export default SiteSettingsPage;
